/** DLP の画面で共通に使う問い合わせと小道具。クエリキーは `["dlp", ...]`。 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";
import { useCallback } from "react";

import { useApi, useMe } from "../../lib/useApi";
import { hasFeature } from "../../lib/features";
import { hasRole } from "../../lib/roles";
import { SCAN_ACTIVE } from "./constants";

/** ボタンの出し分け(権限の真実は API 側)。ロールは viewer < operator < approver < admin。 */
export function useDlpPerms() {
  const me = useMe();
  return {
    canView: hasRole(me.data, "dlp:viewer"),
    canOperate: hasRole(me.data, "dlp:operator"),
    canApprove: hasRole(me.data, "dlp:approver"),
    canAdmin: hasRole(me.data, "dlp:admin"),
    // 契約に無い機能の問い合わせは 403 になるので、画面側でも出し分ける(mdm と同じ考え方)
    hasExitScan: hasFeature(me.data, "dlp.exit-scan"),
    hasUsb: hasFeature(me.data, "dlp.usb"),
    hasUpload: hasFeature(me.data, "dlp.upload"),
    hasMail: hasFeature(me.data, "dlp.mail"),
    hasMailInbound: hasFeature(me.data, "dlp.mail_inbound"),
    readOnly: me.data?.license.read_only ?? true,
  };
}

export function errorText(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

/** 書いたら dlp と監査のキャッシュをまとめて捨てる。 */
export function useInvalidateDlp() {
  const qc = useQueryClient();
  return useCallback(() => {
    void qc.invalidateQueries({ queryKey: ["dlp"] });
    void qc.invalidateQueries({ queryKey: ["audit"] });
  }, [qc]);
}

/** CSV を落とす。Authorization が要るので fetch して blob にする(mdm の useDownload と同じ)。 */
export function useDlpDownload() {
  const api = useApi();
  return useCallback(
    async (path: string) => {
      try {
        const { blob, filename } = await api.download(path);
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } catch (e) {
        void message.error(errorText(e));
      }
    },
    [api],
  );
}

/** 概要の KPI。スキャン一覧のビュー件数にも使う(件数 API を別に足さない)。 */
export function useDlpOverview(enabled = true) {
  const api = useApi();
  return useQuery({ queryKey: ["dlp", "overview"], queryFn: api.dlpOverview, enabled, staleTime: 30_000 });
}

export function useDlpDevices(params: Record<string, string>, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ["dlp", "devices", params],
    queryFn: () => api.dlpDevices(new URLSearchParams(params)),
    enabled,
  });
}

export function useDlpDevice(id: number | null) {
  const api = useApi();
  return useQuery({
    queryKey: ["dlp", "device", id],
    queryFn: () => api.dlpDevice(id!),
    enabled: !!id,
  });
}

export function useDlpScans(params: Record<string, string>, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ["dlp", "scans", params],
    queryFn: () => api.dlpScans(new URLSearchParams(params)),
    enabled,
  });
}

/**
 * スキャン 1 件。結果はエージェントの report で後から届くので、**まだ終わっていない間だけ**
 * 10 秒ごとに見に行く(poll_sec の既定が 300 なので、それ以上細かくしても意味がない)。
 */
export function useDlpScan(id: number | null) {
  const api = useApi();
  return useQuery({
    queryKey: ["dlp", "scan", id],
    queryFn: () => api.dlpScan(id!),
    enabled: !!id,
    refetchInterval: (q) => (q.state.data && SCAN_ACTIVE.includes(q.state.data.status) ? 10_000 : false),
  });
}

export function useDlpScanFindings(id: number | null, page: number, pageSize: number) {
  const api = useApi();
  return useQuery({
    queryKey: ["dlp", "scan-findings", id, page, pageSize],
    queryFn: () => api.dlpScanFindings(id!, new URLSearchParams({ limit: String(pageSize), offset: String((page - 1) * pageSize) })),
    enabled: !!id,
  });
}

export function useDlpPolicies(enabled = true) {
  const api = useApi();
  return useQuery({ queryKey: ["dlp", "policies"], queryFn: api.dlpPolicies, enabled });
}

export function useDlpUsbEvents(params: Record<string, string>, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ["dlp", "usb-events", params],
    queryFn: () => api.dlpUsbEvents(new URLSearchParams(params)),
    enabled,
  });
}

export function useDlpUploadEvents(params: Record<string, string>, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ["dlp", "upload-events", params],
    queryFn: () => api.dlpUploadEvents(new URLSearchParams(params)),
    enabled,
  });
}

export function useDlpUploadDomains(params: Record<string, string>, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ["dlp", "upload-domains", params],
    queryFn: () => api.dlpUploadDomains(new URLSearchParams(params)),
    enabled,
  });
}

export function useDlpSettings(enabled = true) {
  const api = useApi();
  return useQuery({ queryKey: ["dlp", "settings"], queryFn: api.dlpSettings, enabled });
}
